import { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [consultationForm, setConsultationForm] = useState({
    name: '',
    email: '',
    phone: '',
    service: 'Social Media Management',
    preferred_date: '',
    message: '',
  });
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [loading, setLoading] = useState({ contact: false, consultation: false, newsletter: false });

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setLoading({ ...loading, contact: true });
    try {
      const response = await axios.post(`${API}/contact`, contactForm);
      toast.success(response.data.message);
      setContactForm({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading({ ...loading, contact: false });
    }
  };

  const handleConsultationSubmit = async (e) => {
    e.preventDefault();
    setLoading({ ...loading, consultation: true });
    try {
      const response = await axios.post(`${API}/consultation`, consultationForm);
      toast.success(response.data.message);
      setConsultationForm({
        name: '',
        email: '',
        phone: '',
        service: 'Social Media Management',
        preferred_date: '',
        message: '',
      });
    } catch (error) {
      toast.error('Failed to book consultation. Please try again.');
    } finally {
      setLoading({ ...loading, consultation: false });
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setLoading({ ...loading, newsletter: true });
    try {
      const response = await axios.post(`${API}/newsletter`, { email: newsletterEmail });
      toast.success(response.data.message);
      setNewsletterEmail('');
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setLoading({ ...loading, newsletter: false });
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal-600 mb-4 block">
            Get In Touch
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-6">
            Let's Grow Your Business Together
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Ready to take your digital marketing to the next level? Contact us today for a free consultation.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Info */}
          <div className="space-y-6">
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6" data-testid="contact-info-email">
              <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mb-4">
                <Mail className="w-6 h-6 text-teal-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Email Us</h3>
              <a
                href="mailto:ashvyadigitalmedia@gmail.com"
                className="text-teal-600 hover:text-teal-700 font-medium"
              >
                ashvyadigitalmedia@gmail.com
              </a>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6" data-testid="contact-info-phone">
              <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mb-4">
                <Phone className="w-6 h-6 text-teal-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Call Us</h3>
              <a href="tel:9068922387" className="text-teal-600 hover:text-teal-700 font-medium text-lg">
                9068922387
              </a>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6" data-testid="contact-info-location">
              <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mb-4">
                <MapPin className="w-6 h-6 text-teal-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Visit Us</h3>
              <p className="text-slate-600">Modinagar, Ghaziabad, Uttar Pradesh</p>
            </div>

            {/* Newsletter */}
            <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl p-6 text-white">
              <h3 className="font-bold text-xl mb-2">Subscribe to Our Newsletter</h3>
              <p className="text-teal-50 mb-4 text-sm">Get weekly marketing tips and insights!</p>
              <form onSubmit={handleNewsletterSubmit} data-testid="newsletter-form">
                <div className="flex gap-2">
                  <input
                    type="email"
                    placeholder="Your email"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    required
                    className="flex-1 px-4 py-2 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-white"
                    data-testid="newsletter-email-input"
                  />
                  <button
                    type="submit"
                    disabled={loading.newsletter}
                    className="bg-white text-teal-600 px-4 py-2 rounded-xl font-bold hover:bg-slate-100 transition-colors disabled:opacity-50"
                    data-testid="newsletter-submit-btn"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">Send Us a Message</h3>
            <form onSubmit={handleContactSubmit} className="space-y-4" data-testid="contact-form">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Your Name</label>
                <input
                  type="text"
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Email Address</label>
                <input
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  data-testid="contact-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={contactForm.phone}
                  onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  data-testid="contact-phone-input"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Message</label>
                <textarea
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  required
                  rows="4"
                  className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                  data-testid="contact-message-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading.contact}
                className="w-full bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 rounded-xl font-bold transition-colors disabled:opacity-50"
                data-testid="contact-submit-btn"
              >
                {loading.contact ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>

          {/* Consultation Form */}
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">Book a Free Consultation</h3>
            <form onSubmit={handleConsultationSubmit} className="space-y-4" data-testid="consultation-form">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Your Name</label>
                <input
                  type="text"
                  value={consultationForm.name}
                  onChange={(e) => setConsultationForm({ ...consultationForm, name: e.target.value })}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  data-testid="consultation-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Email Address</label>
                <input
                  type="email"
                  value={consultationForm.email}
                  onChange={(e) => setConsultationForm({ ...consultationForm, email: e.target.value })}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  data-testid="consultation-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={consultationForm.phone}
                  onChange={(e) => setConsultationForm({ ...consultationForm, phone: e.target.value })}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  data-testid="consultation-phone-input"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Service Interested In</label>
                <select
                  value={consultationForm.service}
                  onChange={(e) => setConsultationForm({ ...consultationForm, service: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  data-testid="consultation-service-select"
                >
                  <option>Social Media Management</option>
                  <option>Google Ads</option>
                  <option>Lead Generation</option>
                  <option>Creative Reels</option>
                  <option>Content Marketing</option>
                  <option>Website Development</option>
                  <option>Business Development</option>
                  <option>Meta Ads</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Preferred Date</label>
                <input
                  type="date"
                  value={consultationForm.preferred_date}
                  onChange={(e) => setConsultationForm({ ...consultationForm, preferred_date: e.target.value })}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  data-testid="consultation-date-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading.consultation}
                className="w-full bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 rounded-xl font-bold transition-colors disabled:opacity-50"
                data-testid="consultation-submit-btn"
              >
                {loading.consultation ? 'Booking...' : 'Book Consultation'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;